import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ContentCopyOutlined,
  CheckOutlined,
} from '@mui/icons-material';
import { agentApi } from '@/services/api';

type ConnectionStatus = 'connecting' | 'connected' | 'disconnected' | 'error';

interface ConsoleLine {
  type: 'user' | 'agent' | 'system' | 'log';
  text: string;
}

function statusChipProps(status: ConnectionStatus): {
  label: string;
  color: 'success' | 'error' | 'default';
} {
  switch (status) {
    case 'connected':
      return { label: 'Connected', color: 'success' };
    case 'disconnected':
      return { label: 'Disconnected', color: 'error' };
    case 'error':
      return { label: 'Error', color: 'error' };
    case 'connecting':
    default:
      return { label: 'Connecting…', color: 'default' };
  }
}

export default function SessionConsolePage() {
  const { sessionId } = useParams<{ sessionId: string }>();

  const [token, setToken] = useState<string | null>(null);
  const [tokenError, setTokenError] = useState<string | null>(null);
  const [lines, setLines] = useState<ConsoleLine[]>([]);
  const [status, setStatus] = useState<ConnectionStatus>('connecting');
  const [copied, setCopied] = useState(false);

  // Fetch console_token via authenticated API — keeps token out of the page URL
  useEffect(() => {
    if (!sessionId) return;
    agentApi
      .getSessionConsoleToken(sessionId)
      .then(({ console_token }) => setToken(console_token))
      .catch(() =>
        setTokenError('Could not load session console — session not found or access denied')
      );
  }, [sessionId]);

  const scrollRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new lines arrive
  useEffect(() => {
    const el = scrollRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, [lines]);

  const terminalRef = useRef(false); // true once session_stopped fires — no reconnect needed

  // SSE connection with auto-reconnect every 5s on drop
  useEffect(() => {
    if (!sessionId || !token) return;

    let es: EventSource;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

    function connect() {
      terminalRef.current = false;
      const url = `/api/v1/sessions/${encodeURIComponent(sessionId!)}/stream?token=${encodeURIComponent(token!)}`;
      es = new EventSource(url);
      setStatus('connecting');

      es.onopen = () => {
        setStatus('connected');
      };

      es.addEventListener('session_init', (event: MessageEvent) => {
        setStatus('connected');
        try {
          const data = JSON.parse(event.data as string) as { status: string };
          if (data.status === 'STOPPED' || data.status === 'EXPIRED') {
            terminalRef.current = true;
            setLines((prev) => [
              ...prev,
              { type: 'system', text: `Session is ${data.status.toLowerCase()}.` },
            ]);
          }
        } catch {
          // ignore parse errors
        }
      });

      es.addEventListener('turn', (event: MessageEvent) => {
        setStatus('connected');
        try {
          const turn = JSON.parse(event.data as string) as {
            message?: string;
            response?: string;
            session_log?: string;
          };
          const newLines: ConsoleLine[] = [];
          if (turn.message) {
            newLines.push({ type: 'user', text: `> ${turn.message}` });
          }
          if (turn.session_log) {
            newLines.push({ type: 'log', text: turn.session_log });
          } else if (turn.response) {
            newLines.push({ type: 'agent', text: turn.response });
          }
          setLines((prev) => [...prev, ...newLines]);
        } catch {
          setLines((prev) => [...prev, { type: 'system', text: event.data as string }]);
        }
      });

      es.addEventListener('session_stopped', () => {
        terminalRef.current = true;
        setLines((prev) => [...prev, { type: 'system', text: '--- session ended ---' }]);
        setStatus('disconnected');
        es.close();
      });

      es.addEventListener('error', (event: MessageEvent) => {
        try {
          const data = JSON.parse(event.data as string) as { message: string };
          setLines((prev) => [...prev, { type: 'system', text: `Error: ${data.message}` }]);
        } catch {
          // ignore
        }
      });

      es.onerror = () => {
        setStatus((prev) => (prev === 'connecting' ? 'error' : 'disconnected'));
        es.close();
        if (!terminalRef.current) {
          reconnectTimer = setTimeout(connect, 5000);
        }
      };
    }

    connect();

    return () => {
      terminalRef.current = true; // prevent reconnect on unmount
      if (reconnectTimer) clearTimeout(reconnectTimer);
      es.close();
    };
  }, [sessionId, token]);

  const handleCopy = async () => {
    if (lines.length === 0) return;
    try {
      await navigator.clipboard.writeText(lines.map((l) => l.text).join('\n'));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard API may be unavailable in some contexts — silently ignore
    }
  };

  // --- Loading / error guards ---
  if (!sessionId) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 4 }}>
        <Alert severity="error" sx={{ maxWidth: 480 }}>Missing session ID</Alert>
      </Box>
    );
  }

  if (tokenError) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 4 }}>
        <Alert severity="error" sx={{ maxWidth: 480 }}>{tokenError}</Alert>
      </Box>
    );
  }

  if (!token) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 4 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  const { label, color } = statusChipProps(status);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#121212',
        display: 'flex',
        flexDirection: 'column',
        p: { xs: 2, sm: 3 },
      }}
    >
      {/* Header bar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography
            variant="subtitle2"
            sx={{ color: '#8899aa', fontFamily: 'monospace', fontSize: '0.8rem' }}
          >
            Session: {sessionId}
          </Typography>
          <Chip label={label} color={color} size="small" sx={{ fontWeight: 600 }} />
        </Box>

        <Tooltip title={copied ? 'Copied!' : 'Copy output'}>
          <span>
            <IconButton
              size="small"
              onClick={handleCopy}
              disabled={lines.length === 0}
              sx={{ color: copied ? 'success.main' : '#8899aa' }}
              aria-label="Copy console output"
            >
              {copied ? (
                <CheckOutlined fontSize="small" />
              ) : (
                <ContentCopyOutlined fontSize="small" />
              )}
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* Terminal box */}
      <Paper
        elevation={0}
        ref={scrollRef}
        sx={{
          flex: 1,
          backgroundColor: '#1e1e1e',
          borderRadius: 2,
          p: 2,
          maxHeight: '80vh',
          overflowY: 'auto',
          border: '1px solid #2a2a2a',
        }}
      >
        {lines.length === 0 && (status === 'connecting' || status === 'connected') && (
          <Typography
            component="span"
            sx={{
              color: '#5c7a8a',
              fontFamily: 'monospace',
              fontSize: '0.85rem',
            }}
          >
            Waiting for output…
          </Typography>
        )}

        {lines.length === 0 && (status === 'disconnected' || status === 'error') && (
          <Typography
            component="span"
            sx={{
              color: '#e05c5c',
              fontFamily: 'monospace',
              fontSize: '0.85rem',
            }}
          >
            {status === 'error'
              ? 'Connection failed. The session may not exist or the token is invalid.'
              : 'Stream ended with no output.'}
          </Typography>
        )}

        {lines.map((line, idx) => (
          <Box
            key={idx}
            component="pre"
            sx={{
              margin: 0,
              fontFamily: 'monospace',
              fontSize: '0.85rem',
              color:
                line.type === 'user'
                  ? '#7ec8e3'
                  : line.type === 'system'
                    ? '#888'
                    : '#d4d4d4',
              lineHeight: 1.6,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {line.text}
          </Box>
        ))}

        {lines.length > 0 && status === 'error' && (
          <Box
            component="pre"
            sx={{
              margin: 0,
              mt: 1,
              fontFamily: 'monospace',
              fontSize: '0.85rem',
              color: '#e05c5c',
              lineHeight: 1.6,
            }}
          >
            --- connection error ---
          </Box>
        )}
      </Paper>
    </Box>
  );
}
